import React from "react";
import ReactCountryFlag from "react-country-flag";
import { Icon, Tooltip } from "../../components/ui";
import { TOP_PICK, ageOf, ch24ListingId, daowatchesPreview, flagCode, gapPct, money, regionTag } from "./dealsData";
import { DealCard, Medians } from "./dealsParts";
import { ContactSellerAction } from "./ContactSeller";

/**
 * One Chrono24 private-seller listing, on the Stock Alert card (Alex, 2026-11-02: "card similar
 * to the one on Stock alert — reference, brand, model below image"), via DealCard like Auctions:
 *
 *   - photo: a price drop since first seen alone on the top-left line, then ★ Top pick and how
 *     long the listing has been in the report (New / 5d / ↻ 12d);
 *   - below it: reference, brand · model, specs;
 *   - asking price and suggested offer, then Trade 12M (with the % under / over it) | Market 12M;
 *   - footer: the seller's country flag in front of Private seller · Chrono24 / the country,
 *     then Contact and Open on Chrono24.
 */

// The report sends slugs ("very-good", "watch-box-papers"); a card reads them as words.
const words = (slug) => {
  const s = String(slug || "").replace(/[-_]+/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
};
const KIT = { "watch-box-papers": "Full set", "watch-only": "Watch only", "watch-papers": "Papers", "watch-box": "Box" };

const DAY = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const dayLabel = (iso) => DAY.format(new Date(`${String(iso).slice(0, 10)}T12:00:00Z`));

// "DE" → "Germany"; the code itself where the browser has no names.
const countryName = (code) => {
  try {
    return new Intl.DisplayNames(["en-GB"], { type: "region" }).of(code) || code;
  } catch {
    return code;
  }
};

// The Top pick label's tooltip: each rule (dealsData isTopPick) with this listing's own value,
// then what to do (Alex, 2026-10-29: "when we hover over that label, show the criteria").
// The rule is still the report's 3M discount, so the tooltip says so.
const TopPickTip = ({ o }) => {
  const leads = Number(o.b2b_leads) || 0;
  const rules = [
    `${Number(o.discount_pct).toFixed(1)}% under 3-month trade (${money(o.trade_median_3m)}) · needs ${TOP_PICK.minGap}–${TOP_PICK.maxGap}%`,
    "Reliable 3-month trade price",
    o.brand_group === "core" ? `Core brand${leads ? ` · ${leads} B2B lead${leads === 1 ? "" : "s"}` : ""}` : `${leads} B2B lead${leads === 1 ? "" : "s"} for this reference`,
    "Seller not contacted yet",
  ];
  return (
    <span className="ps-tip__body">
      <strong>Why it's a top pick</strong>
      <span className="ps-tip__list">{rules.map((r) => <span key={r}>✓ {r}</span>)}</span>
      <span className="ps-tip__act">
        Act today: check the photos and condition, then Contact and aim for {o.suggested_offer != null ? money(o.suggested_offer) : "the Offer price"} or less.
      </span>
    </span>
  );
};

export default function SellerCard({ o, top = false }) {
  const age = ageOf(o);
  const h = o.history || null;
  const drop = h?.price_drop > 0 ? h.price_drop : null;
  const label = [o.brand, o.model, o.reference].filter(Boolean).join(" ");
  const region = regionTag(o.country);
  // The seller's own country when the box sends it (Alex, 2026-11-02); the region's flag until then.
  const flag = o.country_code || flagCode(o.country);
  const place = o.country_code ? countryName(o.country_code) : region;
  const specs = [o.year, words(o.condition), KIT[o.box_papers] || words(o.box_papers)].filter(Boolean).join(" · ");
  const gap = gapPct(o.trade_median_12m, o.price_gbp ?? o.listing_price);

  return (
    <DealCard
      label={label}
      image={o.image}
      badges={age || top || drop ? <>
        {/* The price drop leads the photo's top-left corner on a line of its own (Alex, 2026-11-02). */}
        {drop ? <span className="deals-cl__drop" title={`Cheaper than when first seen on ${dayLabel(h.first_seen)}`}>↓ {money(drop)} since {dayLabel(h.first_seen)}</span> : null}
        {drop && (age || top) ? <span className="deals-cl__break" aria-hidden="true" /> : null}
        {top ? (
          <Tooltip side="bottom" className="ps-tip" content={<TopPickTip o={o} />}>
            <span className="ps-card__top" tabIndex={0} aria-label="Top pick: why">★ Top pick</span>
          </Tooltip>
        ) : null}
        {age ? (
          <span className={age.kind === "new" ? "deals-cl__fresh" : "deals-cl__age"} title={h?.first_seen ? `In the report since ${dayLabel(h.first_seen)}` : "Listed in the last 48h"}>
            {age.label}
          </span>
        ) : null}
      </> : null}
      flag={(
        <span className="deals-cl__flag" title={place}>
          {flag ? <ReactCountryFlag countryCode={flag} svg aria-label={place} /> : <Icon name="Globe" size="xs" />}
        </span>
      )}
      reference={o.reference}
      refHref={daowatchesPreview(o.product_id)}
      name={[o.brand, o.model].filter(Boolean).join(" · ")}
      specs={specs}
      prices={<>
        <span className="deals-cl__line">
          <strong title="Asking price">{money(o.listing_price)}</strong>
          {o.suggested_offer != null
            ? <span className="ps-card__offer" title={`Offer ${money(o.suggested_offer)} · 12M ${money(o.suggested_offer_12m)}`}>Offer <strong className="deals-cl__offer">{money(o.suggested_offer)}</strong></span>
            : <span className="deals-cl__muted">No trade data</span>}
        </span>
        <Medians trade={o.trade_median_12m} market={o.market_median_12m} gap={gap} />
      </>}
      who="Private seller · Chrono24"
      whoSub={<span className="deals-cl__who-phone">{place}</span>}
      action={{ label: "Open on Chrono24", href: o.listing_url || "#" }}
      extra={ch24ListingId(o.listing_url)
        ? <ContactSellerAction compact url={o.listing_url} label={label} productId={o.product_id}
            summary={{ image: o.image, price: money(o.listing_price), meta: `Private seller · ${place}` }} />
        : null}
    />
  );
}
